import { z } from 'zod';

import { WindowKeySchema } from './enums';
import { MarketDateSchema, TickerSchema } from './primitives';

/** ~3Y trading days with headroom; chart may decimate client-side. */
export const BARS_MAX = 2_000;

/**
 * Adjusted-close window. `bars` stay a number array so every existing
 * metric/chart consumer is unchanged. `sessions` is the aligned US market
 * date for each bar (`YYYY-MM-DD`), same length and order — a parallel
 * array rather than `{ on, close }[]` so the close vector remains one
 * symbol (ADR-0037).
 */
export const PriceSeriesSchema = z
  .object({
    ticker: TickerSchema,
    window: WindowKeySchema,
    /** Adjusted close bars, ascending; synthetic length is WN[window]. */
    bars: z.array(z.number()).min(1).max(BARS_MAX),
    /**
     * Market dates aligned 1:1 with `bars`. Shape-only here; calendar
     * validity is ingest's contract (`MarketDateSchema`).
     */
    sessions: z.array(MarketDateSchema).min(1).max(BARS_MAX),
  })
  .superRefine((val, ctx) => {
    if (val.sessions.length !== val.bars.length) {
      ctx.addIssue({
        code: 'custom',
        message: 'sessions must align 1:1 with bars',
        path: ['sessions'],
      });
    }
  });
export type PriceSeries = z.infer<typeof PriceSeriesSchema>;

export const SeriesStatsSchema = z.object({
  first: z.number(),
  last: z.number(),
  min: z.number(),
  max: z.number(),
  hi52w: z.number(),
  lo52w: z.number(),
  /**
   * Mean of non-null daily volumes over the same 1Y window as hi52w/lo52w.
   * A missing observation is null, never 0 (ADR-0034 · R2b). A mean of 0 is a
   * real window of halted sessions.
   */
  avgVolume: z.number().nonnegative().nullable(),
});
export type SeriesStats = z.infer<typeof SeriesStatsSchema>;
